import { useState, useEffect } from 'react';
import { Message, Tab } from '../../types/chat';
import { loadTabsFromStorage, saveTabsToStorage } from '../../utils/Chat/index';
import { deleteMessagesForTopic } from '../../utils/MessageHistory.js';
interface UseChatTabsProps {
  topic: string | null;
  getMessagesForTopic: (topic: string) => Message[];
  storageKey: string;
}
export const useChatTabs = ({ topic, getMessagesForTopic, storageKey }: UseChatTabsProps) => {
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeTab, setActiveTab] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tabToDelete, setTabToDelete] = useState<string | null>(null);
  useEffect(() => {
    const savedTabs = loadTabsFromStorage({ STORAGE_KEY: storageKey });
    if (savedTabs.length > 0) {
      setTabs(savedTabs);
      setActiveTab(savedTabs[0].id);
    }
  }, []);
  useEffect(() => {
    if (tabs.length > 0) {
      saveTabsToStorage({ STORAGE_KEY: storageKey }, tabs);
    } else {
      localStorage.removeItem(storageKey);
    }
  }, [tabs]);
  useEffect(() => {
    if (topic && !tabs.find(tab => tab.topic === topic)) {
      const messages = getMessagesForTopic(topic);
      const messagesWithTimestamps = messages.map(msg => ({
        ...msg,
        timestamp: msg.timestamp || new Date(),
      }));
      const newTab: Tab = {
        id: Date.now().toString(),
        topic,
        messages: messagesWithTimestamps,
      };
      setTabs(prev => [newTab, ...prev]);
      setActiveTab(newTab.id);
    } else if (topic) {
      const existingTab = tabs.find(tab => tab.topic === topic);
      if (existingTab) {
        setActiveTab(existingTab.id);
      }
    }
  }, [topic]);
  const handleConfirmDelete = () => {
    if (tabToDelete) {
      const tabToRemove = tabs.find(tab => tab.id === tabToDelete);
      if (tabToRemove) {
        deleteMessagesForTopic(tabToRemove.topic);
      }
      const newTabs = tabs.filter(tab => tab.id !== tabToDelete);
      setTabs(newTabs);
      if (activeTab === tabToDelete) {
        setActiveTab(newTabs[0]?.id || '');
      }
      if (newTabs.length > 0) {
        saveTabsToStorage({ STORAGE_KEY: storageKey }, newTabs);
      } else {
        localStorage.removeItem(storageKey);
      }
    }
    setDeleteDialogOpen(false);
    setTabToDelete(null);
  };
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTabToDelete(null);
  };
  const handleCloseTab = (tabId: string) => {
    setTabToDelete(tabId);
    setDeleteDialogOpen(true);
  };
  const truncateTopic = (text: string, maxLength: number | { xs: number; md: number } = 30) => {
    const length = typeof maxLength === 'number' ? maxLength : maxLength.md;
    if (text.length <= length) return text;
    return text.slice(0, length) + '...';
  };
  return {
    tabs,
    setTabs,
    activeTab,
    setActiveTab,
    deleteDialogOpen,
    setDeleteDialogOpen,
    tabToDelete,
    setTabToDelete,
    handleConfirmDelete,
    handleCancelDelete,
    handleCloseTab,
    truncateTopic,
  };
};
